/* eslint-disable max-lines */
// eventBufferUtil.js

import isMobile, { getBrowserName, getOperatingSystem } from "../../utils/common";
import { getPageNameForCleverTap } from "../../utils/fnpPageType";
import CookieUtil from "../../utils/storage/cookieUtil";

/**
 * A utility for buffering events across multiple tabs, with one tab elected as leader
 * to perform flushes (send events to backend). Includes leader re-election,
 * failure handling, unload, etc.
 */
export function createEventBuffer({
  maxBatch = 20,
  flushInterval = 10000, // ms between periodic flush attempts
  storageKey = "eventBufferQueue",
  backendUrl = "/events", // URL to send batched events
  lockName = "event-buffer-leader-lock",
} = {}) {
  const SESSION_START_FLAG = "eventBufferSessionStartDone";
  const SESSION_START_EVENT_NAME = "Custom Session Start"; // unified name, no spaces

  const cookieTTL = 20 * 60; // 20 minutes
  const options = {
    maxAge: cookieTTL,
    path: "/",
  };

  /** */
  function loadQueue() {
    if (typeof window === "undefined") {
      return [];
    }
    try {
      const stored = localStorage?.getItem(storageKey);
      const parsed = stored ? JSON?.parse(stored) : [];
      return parsed;
    } catch (e) {
      console.error("[EventBuffer] loadQueue: invalid JSON or other error", e);
      return [];
    }
  }

  let queue = loadQueue();

  /** */
  function saveQueue() {
    if (typeof window === "undefined") return;
    try {
      localStorage?.setItem(storageKey, JSON?.stringify(queue));
    } catch (e) {
      console.error("[EventBuffer] saveQueue: error saving to localStorage", e);
    }
  }

  /** */
  function markSessionStartDone() {
    if (typeof window === "undefined") return;
    try {
      localStorage?.setItem(SESSION_START_FLAG, "true");
    } catch (e) {
      console.error("[EventBuffer] markSessionStartDone: error setting flag", e);
    }
  }

  /** */
  function isSessionStartDone() {
    if (typeof window === "undefined") return false;
    try {
      return localStorage?.getItem(SESSION_START_FLAG) === "true";
    } catch (e) {
      console.error("[EventBuffer] isSessionStartDone: error reading flag", e);
      return false;
    }
  }

  /** */
  function ensureQueueIntegrity() {
    if (typeof window === "undefined") return;
    const currentQueue = loadQueue();
    if (currentQueue?.length > queue?.length) {
      queue = currentQueue;
    }
  }

  /** */
  const getSessionID = () => {
    const timeStamp = Date.now();
    return `${isMobile() ? "m" : "d"}_${timeStamp}_${Math.floor(Math.random() * 100)}`;
  };

  /** */
  function generateUUID() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c == "x" ? r : (r & 0x3) | 0x8;
      return v?.toString(16);
    });
  }

  /** */
  const setCookiesAndIds = () => {
    if (typeof window === "undefined") return;
    const sessionId = CookieUtil?.getCookie("cdpSessionID");
    const uniqueId = localStorage?.getItem("cdpClientId");
    if (!uniqueId) {
      const newUniqueId = generateUUID();
      localStorage?.setItem("cdpClientId", newUniqueId);
    }
    const sessionStartTs = sessionId?.split("_")?.[1];
    if (!sessionId || !sessionStartTs) {
      const newSessionId = getSessionID();
      CookieUtil?.setCookie("cdpSessionID", newSessionId, options);
    }
  };

  setCookiesAndIds();

  /** */
  function maybeInsertSessionStart() {
    if (typeof window === "undefined") {
      return;
    }
    if (isSessionStartDone()) {
      return;
    }
    const alreadyInQueue = queue?.some((ev) => ev.event_name === SESSION_START_EVENT_NAME);
    if (!alreadyInQueue) {
      const sessionId = CookieUtil?.getCookie("cdpSessionID") || "";
      const sessionStartTs = sessionId?.split("_")?.[1];
      const uniqueId = localStorage?.getItem("cdpClientId");
      const sessionEvent = {
        event_name: SESSION_START_EVENT_NAME,
        browser: getBrowserName(),
        complete_url: window.location.href,
        clean_url: `${window.location.pathname}`,
        page_name: getPageNameForCleverTap(window?.fnpPageType),
        operating_system: getOperatingSystem() || "unknown",
        page_type: window.fnpPageType,
        session_id: sessionId,
        session_start_ts: sessionStartTs,
        device_id: uniqueId,
        ts: Date.now(),
      };
      queue?.unshift(sessionEvent);
      saveQueue();
    }
  }

  maybeInsertSessionStart();

  let isLeader = false;
  let flushTimer = null;
  let leaderTimer = null;
  let leadershipRelease = null;
  const channel = typeof window !== "undefined" ? new BroadcastChannel("event-buffer") : null;
  let hasRequestedLock = false;

  // Interval fallback logic
  let currentInterval = flushInterval;
  const FALLBACK_INTERVAL = 60000; // 1 minute

  /** */
  function becomeLeader() {
    if (!isLeader) {
      isLeader = true;
    }
  }

  /** */
  async function maybeAcquireLeadershipUsingLock() {
    if (typeof window === "undefined") return false;
    if (!navigator?.locks || !navigator?.locks?.request) return false;
    try {
      hasRequestedLock = true;
      await navigator?.locks?.request(lockName, { mode: "exclusive" }, async () => {
        becomeLeader();
        await new window.Promise((resolve) => {
          leadershipRelease = resolve;
        });
      });
      isLeader = false;
      leadershipRelease = null;
      return true;
    } catch (err) {
      console.error("[EventBuffer] maybeAcquireLeadershipUsingLock error", err);
      return false;
    }
  }

  /** */
  function fallbackLeaderElection() {
    if (typeof window === "undefined") return;
    const leaderKey = "eventBufferLeader";
    const now = Date.now();
    const existing = localStorage?.getItem(leaderKey);
    const existingTs = existing ? parseInt(existing, 10) : null;
    const LEADER_TIMEOUT = flushInterval * 2;

    if (!existingTs || now - existingTs > LEADER_TIMEOUT) {
      localStorage?.setItem(leaderKey, String(now));
      becomeLeader();
    } else {
      isLeader = false;
    }

    if (isLeader) {
      try {
        localStorage?.setItem(leaderKey, String(Date.now()));
      } catch (e) {
        console.warn("[EventBuffer] fallbackLeaderElection: failed updating leader timestamp", e);
      }
    }
  }

  /** */
  function setupLeaderElection() {
    if (typeof window === "undefined") {
      return;
    }
    if (navigator?.locks && navigator?.locks?.request && !hasRequestedLock) {
      maybeAcquireLeadershipUsingLock();
    } else {
      fallbackLeaderElection();
      leaderTimer = setInterval(() => {
        fallbackLeaderElection();
      }, flushInterval);
    }
  }

  /** */
  function setupChannelListener() {
    if (typeof window === "undefined" || !channel) return;
    channel.onmessage = (msg) => {
      const data = msg?.data;
      if (!data || typeof data?.type !== "string") return;
      if (data?.type === "flushed") {
        // Only reload queue if we're not the leader who sent this message
        if (!isLeader) {
          queue = loadQueue();
        }
      } else if (data?.type === "clearQueue") {
        queue = [];
        saveQueue();
      }
    };
  }

  // Helper to manage interval changes
  /** */
  function setFlushTimer(newInterval) {
    if (flushTimer) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
    flushTimer = setInterval(() => {
      if (isLeader) {
        // eslint-disable-next-line no-use-before-define
        flush(false);
      }
    }, newInterval);
    currentInterval = newInterval;
  }

  /** */
  async function compressJson(jsonObject) {
    const jsonString = JSON.stringify(jsonObject);
    const stream = new Blob([jsonString]).stream();

    const compressedStream = stream.pipeThrough(new CompressionStream("gzip"));

    const compressedBlob = await new Response(compressedStream).blob();
    return compressedBlob;
  }

  /** */
  async function flush(isUnload = false) {
    if (typeof window === "undefined") return;
    if (!isLeader) {
      return;
    }

    ensureQueueIntegrity();

    if (queue?.length === 0) return;

    const batch = [...queue];

    if (!isUnload) {
      queue = [];
      saveQueue();
    }

    try {
      const compressedData = await compressJson(batch);
      const responseRaw = await fetch(backendUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Content-Encoding": "gzip" },
        body: compressedData,
        keepalive: isUnload,
      });
      const response = await responseRaw.json();

      if (response?.errorCount) {
        // If not already in fallback, switch to fallback interval
        if (currentInterval !== FALLBACK_INTERVAL) {
          setFlushTimer(FALLBACK_INTERVAL);
        }
        throw new Error(`Flush failed, status ${responseRaw?.status}`);
      } else {
        // If in fallback, restore to base interval
        if (currentInterval !== flushInterval) {
          setFlushTimer(flushInterval);
        }
      }

      const sentSessionStart = batch?.some((ev) => ev?.event_name === SESSION_START_EVENT_NAME);
      if (sentSessionStart) {
        markSessionStartDone();
      }
      if (!isUnload && channel) {
        channel?.postMessage({ type: "flushed" });
      }
    } catch (err) {
      console.error("[EventBuffer] flush failed", err);
      if (!isUnload) {
        queue = [...batch, ...queue];
        saveQueue();
      }
    }
  }

  /** */
  function setupFlushTimer() {
    if (typeof window === "undefined") return;
    setFlushTimer(currentInterval);
  }

  /** */
  function shutdown() {
    if (flushTimer) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
    if (leaderTimer) {
      clearInterval(leaderTimer);
      leaderTimer = null;
    }
    if (leadershipRelease) {
      leadershipRelease();
      leadershipRelease = null;
    }
    if (channel) {
      channel?.close();
    }
    if (typeof window !== "undefined" && (!navigator?.locks || !navigator?.locks?.request)) {
      try {
        localStorage?.removeItem("eventBufferLeader");
      } catch (e) {
        console.warn("[EventBuffer] Could not clean up leader key", e);
      }
    }
  }

  /** */
  function track(event) {
    if (typeof window === "undefined") return;
    const sessionId = CookieUtil?.getCookie("cdpSessionID") || "";
    const sessionStartTs = sessionId?.split("_")?.[1];
    const uniqueId = localStorage?.getItem("cdpClientId");

    const ev = {
      event_name: event?.event_name || event?.name || "unknown",
      complete_url: window.location.href,
      clean_url: `${window.location.pathname}`,
      browser: getBrowserName(),
      os: getOperatingSystem() || "unknown",
      session_id: sessionId,
      session_start_ts: sessionStartTs,
      ts: Date.now(),
      device_id: uniqueId,
      ...event,
    };

    queue?.push(ev);
    saveQueue();

    // Only flush if we're leader and queue is at max batch size
    if (isLeader && queue?.length >= maxBatch) {
      flush(false);
    }
  }

  // Initialization
  setupLeaderElection();
  setupChannelListener();
  setupFlushTimer();

  return {
    track,
    flush,
    isLeader: () => isLeader,
    getQueue: () => [...queue],
    shutdown,
  };
}

export default createEventBuffer;
